import React from "react";
import { Globe, ArrowUpDown } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { SectionCard, Button } from "../primitives";
import { AttentionCard, ToolsList } from "../classic/panels";
import { ClassicMotion } from "../classic/micro";
import { PulseMotion } from "./motion";
import { HealthBand, ProjectsCard, PeopleQuadrant, DayDial, RhythmCard, WeekHeatmap, SplitCard } from "./panels";

/**
 * PulseOverview — the redesigned Classic overview.
 *
 * Reading order is a manager's question order: how are we doing → where did
 * the effort go → who is carrying it → what do I do → what is the rhythm.
 *
 * Every figure comes from data the page already loads. Three things the
 * approved mock showed have NO backing field and are deliberately absent
 * rather than faked:
 *   · the previous period's hour curve (the dial's ghost ticks) — the
 *     overview read returns `hourCurve` for the current window only;
 *   · an idle breakdown by reason (meetings / logged breaks / unexplained);
 *   · a multi-week trend of unexplained idle.
 * `SplitCard` covers that slot with what the payload does carry: the
 * active/idle split, tool switching, and how much of the org has app-level
 * detail behind those numbers.
 */
export function PulseOverview({
  d, classic, onDrill, onTool, goTeam, goProjects, goAppUsage, openMember,
}) {
  const t = useDashTheme();
  const rangeDays = classic.overview?.range?.days
    || Math.max(1, Math.round((new Date(d.range.end) - new Date(d.range.start)) / 86400000) + 1);

  const handleMember = React.useCallback((p) => {
    if (!openMember) return;
    openMember({ userId: p.userId, name: p.name, position: p.position, avatar: p.avatar });
  }, [openMember]);

  /* Three states look identical if you only draw empty panels: the read
     FAILED, the read succeeded but the window is genuinely empty, and the
     read is still in flight. Say which — a silent page of "no data" is the
     single most confusing thing a dashboard can do. */
  const tracked = classic.overview?.totals?.trackedMinutes;
  const notice = classic.error
    ? {
      tone: "error",
      title: "Could not load this period",
      body: classic.error,
      hint: "The panels below are empty because the read failed, not because the org was idle.",
      retry: classic.reload,
    }
    : (!classic.loading && classic.emptyReason)
      ? {
        /* The server answered, and told us why it had nothing. "No accessible
           members" is a permissions answer, not a quiet week — completely
           different fix, so do not blur the two into one message. */
        tone: "empty",
        title: "The server returned no data for this period",
        body: classic.emptyReason,
        hint: /accessible|scope|member/i.test(classic.emptyReason)
          ? "This is an access-scope answer, not an idle team — your account currently resolves to no visible members."
          : "Widen the date range above, or clear the member filter, to see more.",
        retry: classic.reload,
      }
      : (!classic.loading && classic.overview && !tracked)
        ? {
          tone: "empty",
          title: "Nothing was tracked in this window",
          body: `No activity between ${fmtDay(d.range.start)} and ${fmtDay(d.range.end)}`
            + `${d.member ? " for the selected person" : ""}.`,
          hint: "Widen the date range above, or clear the member filter, to see more.",
          retry: null,
        }
        : null;

  return (
    <div className="dashv2-pulse" style={{ display: "flex", flexDirection: "column", gap: t.s[4] }}>
      <PulseMotion />
      {/* The classic keyframes carry `.tgcl-shim` (every loading skeleton on
          this page) plus `tgcl-widen`/`tgcl-grow` used by the classic panels
          reused below. They used to be injected only by <ClassicMotion/>
          inside OverviewTab — which PULSE_OVERVIEW replaces, so on this page
          the rule never existed and EVERY <Shim/> rendered as an invisible
          transparent div. That is why loading looked like blank white cards. */}
      <ClassicMotion />
      <PulseGrid />

      {notice ? <Notice t={t} {...notice} /> : null}

      <HealthBand
        overview={classic.overview}
        goals={d.goals}
        loading={classic.loading}
        onTeam={goTeam}
      />

      <ProjectsCard
        overview={classic.overview}
        loading={classic.loading}
        onDrill={onDrill}
        onViewAll={goProjects}
      />

      <div className="dashv2-pulse-row" style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: t.s[4], alignItems: "start" }}>
        <PeopleQuadrant
          // the WHOLE roster, not the paginated table — this card claims to
          // plot every person, and d.teamRows is only ever one page of eight
          teamRows={d.teamAll && d.teamAll.length ? d.teamAll : d.teamRows}
          loading={d.teamAllLoading}
          rangeDays={rangeDays}
          onMember={handleMember}
          onViewAll={goTeam}
        />
        <AttentionCard attention={classic.attention} loading={classic.loading} onDrill={onDrill} />
      </div>

      <div className="dashv2-pulse-row" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,2fr)", gap: t.s[4], alignItems: "start" }}>
        <DayDial overview={classic.overview} loading={classic.loading} />
        <RhythmCard overview={classic.overview} loading={classic.loading} />
      </div>

      <div className="dashv2-pulse-row" style={{ display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: t.s[4], alignItems: "start" }}>
        <WeekHeatmap overview={classic.overview} loading={classic.loading} />
        <SplitCard overview={classic.overview} loading={classic.loading} />
      </div>

      <SectionCard
        accent="aqua"
        title="What your company works inside"
        subtitle="Apps and websites by time — click a row for the full breakdown"
        icon={<Globe size={16} />}
        padding={0}
        actions={
          <Button variant="ghost" size="sm" onClick={goAppUsage}
            rightIcon={<ArrowUpDown size={13} style={{ transform: "rotate(90deg)" }} />}>
            App usage
          </Button>
        }
      >
        <ToolsList d={d} overview={classic.overview} onTool={onTool} loading={d.mostVisitedLoading} />
      </SectionCard>
    </div>
  );
}

const fmtDay = (x) => {
  try { return new Date(x).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch (_) { return "—"; }
};

/** Says WHY the page is empty. Error and empty are different problems. */
function Notice({ t, tone, title, body, hint, retry }) {
  const bad = tone === "error";
  const accent = bad ? t.status.negative : t.c.textTertiary;
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        display: "flex", alignItems: "flex-start", gap: t.s[3],
        background: t.c.surface, borderRadius: 12, boxShadow: t.shadow.card,
        borderLeft: `3px solid ${accent}`, padding: `${t.s[4]}px ${t.s[5]}px`,
      }}
    >
      <span style={{
        flex: "0 0 auto", width: 22, height: 22, borderRadius: 7, marginTop: 1,
        display: "grid", placeItems: "center", color: accent,
        background: bad ? t.status.negativeSoft : t.c.surfaceMuted,
      }}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
          <circle cx="8" cy="8" r="6.6" /><path d="M8 4.6v4M8 11.1v.1" strokeLinecap="round" />
        </svg>
      </span>
      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
        <b style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.c.text }}>{title}</b>
        <span style={{ display: "block", fontSize: 12, color: t.c.textSecondary, marginTop: 3, wordBreak: "break-word" }}>{body}</span>
        <span style={{ display: "block", fontSize: 11.5, color: t.c.textTertiary, marginTop: 4 }}>{hint}</span>
      </div>
      {retry ? (
        <button type="button" onClick={retry} style={{
          appearance: "none", background: "transparent", border: `1px solid ${t.c.border}`,
          color: t.c.textSecondary, font: "inherit", fontSize: 12, fontWeight: 600,
          padding: "6px 12px", borderRadius: 8, cursor: "pointer", flex: "0 0 auto",
        }}>Try again</button>
      ) : null}
    </div>
  );
}

/* Layout only — the panels themselves are fluid, but the two-up rows have to
   stack before their charts get too narrow to read. Injected here so the
   Pulse view carries its own responsive rules and does not depend on a
   stylesheet the rest of the dashboard shares. */
function PulseGrid() {
  return (
    <style>{`
      @media (max-width: 1180px){
        .dashv2-pulse .dashv2-pulse-row{grid-template-columns:1fr !important}
        .dashv2-pulse .dashv2-pulse-spent{grid-template-columns:1fr !important}
        .dashv2-pulse .dashv2-pulse-verdict{grid-template-columns:minmax(180px,200px) minmax(0,1fr) !important}
      }
      @media (max-width: 820px){
        .dashv2-pulse .dashv2-pulse-verdict{grid-template-columns:1fr !important; justify-items:center; text-align:center}
      }
    `}</style>
  );
}

export default PulseOverview;
